import { useIntl } from 'react-intl'

import { getAgeRange, NpTestPlanDuration } from '../data/npTestPlanMeasureUtils'
import { NpTestPlanInformant, NpTestPlanMeasure, NpTestPlanMeasureType } from '../data/npTestPlanTypes'

export const useNpTestPlanMeasureLabels = () => {
  const { formatMessage } = useIntl()

  const formatAgeRange = (measure: NpTestPlanMeasure): string => {
    const { min, max } = getAgeRange(measure)
    return max === null
      ? formatMessage(
          {
            defaultMessage: '{min}+ years',
            description: 'Age range of a test measure that has no upper age limit',
          },
          { min },
        )
      : formatMessage(
          {
            defaultMessage: '{min}-{max} years',
            description: 'Age range a test measure is appropriate for',
          },
          { min, max },
        )
  }

  const formatDuration = ({ min, max }: NpTestPlanDuration): string =>
    max === null
      ? formatMessage(
          {
            defaultMessage: '{min} min',
            description: 'How long a test measure takes, when the estimate is a single number',
          },
          { min },
        )
      : formatMessage(
          {
            defaultMessage: '{min}-{max} mins',
            description: 'How long a test measure takes, when the estimate is a range',
          },
          { min, max },
        )

  const formatMeasureType = (measureType: NpTestPlanMeasureType): string =>
    measureType === NpTestPlanMeasureType.RATING_SCALE
      ? formatMessage({
          defaultMessage: 'Rating scales',
          description: 'Heading for the questionnaires on a neuropsych test plan',
        })
      : formatMessage({
          defaultMessage: 'Face-to-face tests',
          description: 'Heading for the tests administered in person on a neuropsych test plan',
        })

  const formatInformant = (informant: NpTestPlanInformant): string => {
    switch (informant) {
      case NpTestPlanInformant.SELF_REPORT:
        return formatMessage({
          defaultMessage: 'Client',
          description: 'Label for the test measures the member answers themselves',
        })
      case NpTestPlanInformant.CAREGIVER:
        return formatMessage({
          defaultMessage: 'Caregiver',
          description: 'Label for the test measures a caregiver answers',
        })
      case NpTestPlanInformant.TEACHER:
        return formatMessage({
          defaultMessage: 'Teacher',
          description: 'Label for the test measures a teacher answers',
        })
      case NpTestPlanInformant.THERAPIST:
        return formatMessage({
          defaultMessage: 'Clinician',
          description: 'Label for the test measures a clinician answers',
        })
      case NpTestPlanInformant.OTHER:
        return formatMessage({
          defaultMessage: 'Other',
          description: 'Label for the test measures someone other than the listed informants answers',
        })
    }
  }

  const formatSessionFormat = ({ sessionFormat }: NpTestPlanMeasure): string => {
    if (sessionFormat.startsWith('digital')) {
      return formatMessage({
        defaultMessage: 'Digital',
        description: 'Label for a test measure the member or provider completes on screen',
      })
    }
    if (sessionFormat === 'pdf') {
      return formatMessage({
        defaultMessage: 'PDF',
        description: 'Label for a test measure delivered as a PDF',
      })
    }
    return sessionFormat
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return { formatAgeRange, formatDuration, formatMeasureType, formatInformant, formatSessionFormat }
}
